import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { generateBreadcrumbList } from '../utils/schemaHelpers';

export interface TocItem {
  id: string;
  label: string;
  level: 1 | 2;
}

export interface RelatedCalculator {
  service: string;
  path: string;
  label: string;
}

interface GuideLayoutProps {
  title: string;
  description: string;
  lastUpdated: string;
  tocItems: TocItem[];
  relatedCalculators?: RelatedCalculator[];
  topCta?: React.ReactNode;
  bottomCta?: React.ReactNode;
  children: React.ReactNode;
  // SEO props — when provided, GuideLayout emits Helmet metadata
  canonical?: string;
  metaTitle?: string;
  metaDescription?: string;
}

export default function GuideLayout({
  title,
  description,
  lastUpdated,
  tocItems,
  relatedCalculators = [],
  topCta,
  bottomCta,
  children,
  canonical,
  metaTitle,
  metaDescription,
}: GuideLayoutProps) {
  const location = useLocation();
  const [scrollPct, setScrollPct] = useState(0);
  const [activeId, setActiveId] = useState<string>('');
  const contentRef = useRef<HTMLDivElement>(null);

  // Generate breadcrumb label from URL path
  const getBreadcrumbLabel = (path: string): string => {
    const labels: Record<string, string> = {
      'blob-storage-pricing': 'Blob Storage Pricing Guide',
      'pricing-traps': 'Azure Billing Traps',
      'networking': 'Networking Costs Guide',
      'bandwidth': 'Bandwidth Costs Guide',
      'data-factory': 'Data Factory Pricing Guide',
      'azure-firewall-pricing-uk': 'Azure Firewall Pricing Guide',
      'log-analytics': 'Log Analytics Pricing Guide',
      'virtual-desktop': 'Virtual Desktop Cost Guide',
      'invoice-reconciliation': 'Invoice Reconciliation Guide',
      'search-job-vs-restore': 'Search Jobs vs Restore Guide',
    };
    return labels[path?.split('/').pop() || 'guides'] || 'Guide';
  };

  const breadcrumbLabel = getBreadcrumbLabel(location.pathname);

  // Scroll progress bar — passive listener, no layout reads
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const scrolled = el.scrollTop;
      const total = el.scrollHeight - el.clientHeight;
      setScrollPct(total > 0 ? Math.min(100, (scrolled / total) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Active TOC item — IntersectionObserver avoids forced reflow from getBoundingClientRect
  useEffect(() => {
    if (tocItems.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: '-10% 0px -80% 0px', threshold: 0 }
    );
    const els = tocItems.map((t) => document.getElementById(t.id)).filter(Boolean) as HTMLElement[];
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [tocItems]);

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: 'var(--surface-container-low)',
        color: 'var(--text-primary)'
      }}
    >
      {(canonical || metaTitle || metaDescription) && (
        <Helmet>
          {metaTitle && <title>{metaTitle}</title>}
          {metaDescription && <meta name="description" content={metaDescription} />}
          {canonical && <link rel="canonical" href={canonical} />}
          {canonical && <meta property="og:url" content={canonical} />}
          {metaTitle && <meta property="og:title" content={metaTitle} />}
          {metaDescription && <meta property="og:description" content={metaDescription} />}
          <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
          <meta property="og:type" content="article" />
          <meta name="twitter:card" content="summary_large_image" />
          {canonical && <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: metaTitle ?? title,
            description: metaDescription ?? description,
            url: canonical,
            dateModified: lastUpdated,
            author: { '@type': 'Organization', name: 'AzureCalc.uk', url: 'https://www.azure-calc.co.uk' },
            publisher: { '@type': 'Organization', name: 'AzureCalc.uk', url: 'https://www.azure-calc.co.uk' },
          })}</script>}
          {canonical && <script type="application/ld+json">{JSON.stringify(generateBreadcrumbList([
            { name: 'Home', url: '/', position: 1 },
            { name: 'Guides', url: '/guides', position: 2 },
            { name: breadcrumbLabel, url: canonical.replace('https://www.azure-calc.co.uk', ''), position: 3 },
          ]))}</script>}
        </Helmet>
      )}
      {/* Progress bar */}
      <div
        className="fixed top-12 left-0 right-0 z-40 h-[2px]"
        style={{ backgroundColor: 'var(--surface-container-high)' }}
      >
        <div
          className="h-full bg-[#0078d4] transition-all duration-100"
          style={{ width: `${scrollPct}%` }}
        />
      </div>

      <div className="max-w-[1200px] mx-auto px-4 pt-20 pb-16 flex gap-8">
        {/* TOC Sidebar — desktop only */}
        {tocItems.length > 0 && (
          <aside className="hidden xl:block w-56 shrink-0">
            <div className="sticky top-24">
              <Link
                to="/"
                className="flex items-center gap-1 text-[12px] hover:text-[#0078d4] transition-colors mb-6"
                style={{ color: 'var(--text-tertiary)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#0078d4'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
              >
                <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                Back to calculators
              </Link>

              <p className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: 'var(--text-tertiary)' }}>
                On this page
              </p>
              <nav className="flex flex-col gap-1">
                {tocItems.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className={[
                      'text-[13px] leading-snug transition-colors py-1 border-l-2',
                      item.level === 2 ? 'pl-4' : 'pl-2',
                      activeId === item.id
                        ? 'border-[#0078d4]'
                        : 'border-transparent',
                    ].join(' ')}
                    style={{
                      color: activeId === item.id ? '#c7e0f4' : 'var(--text-tertiary)'
                    }}
                    onMouseEnter={(e) => {
                      if (activeId !== item.id) e.currentTarget.style.color = 'var(--text-secondary)';
                    }}
                    onMouseLeave={(e) => {
                      if (activeId !== item.id) e.currentTarget.style.color = 'var(--text-tertiary)';
                    }}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 max-w-[860px]">
          {/* Back link — mobile */}
          <Link
            to="/"
            className="xl:hidden flex items-center gap-1 text-[12px] hover:text-[#0078d4] transition-colors mb-6"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#0078d4'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            <span className="material-symbols-outlined text-[16px]">arrow_back</span>
            Back to calculators
          </Link>

          {/* Header */}
          <header className="mb-8">
            <h1 className="text-3xl font-extrabold tracking-tight mb-3" style={{ color: 'var(--text-primary)' }}>{title}</h1>
            <p className="text-[16px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>{description}</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
              Prices last verified:{' '}
              <span style={{ color: 'var(--text-tertiary)' }}>{lastUpdated}</span>
            </p>
          </header>

          {/* Top affiliate CTA slot */}
          {topCta && <div className="mb-8">{topCta}</div>}

          {/* Article body */}
          <div
            ref={contentRef}
            className="
              prose-guide
              [&_h2]:text-[22px] [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:scroll-mt-24
              [&_h3]:text-[17px] [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:scroll-mt-24
              [&_p]:text-[15px] [&_p]:leading-relaxed [&_p]:mb-4
              [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:text-[15px]
              [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4 [&_ol]:text-[15px]
              [&_li]:mb-1 [&_li]:leading-relaxed
              [&_code]:font-mono [&_code]:text-[13px] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded
              [&_pre]:border [&_pre]:rounded [&_pre]:p-4 [&_pre]:overflow-x-auto [&_pre]:mb-6
              [&_pre_code]:bg-transparent [&_pre_code]:p-0
              [&_table]:w-full [&_table]:text-[14px] [&_table]:border-collapse [&_table]:mb-6
              [&_th]:text-left [&_th]:font-semibold [&_th]:border-b [&_th]:pb-2 [&_th]:pr-4
              [&_td]:border-b [&_td]:py-2 [&_td]:pr-4
              [&_strong]:font-semibold
              [&_blockquote]:border-l-4 [&_blockquote]:border-[#0078d4] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-6
              [&_hr]:my-8
            "
            style={{
              color: 'var(--text-secondary)'
            }}
          >
            {children}
          </div>

          {/* Bottom affiliate CTA slot */}
          {bottomCta && <div className="mt-10 mb-10">{bottomCta}</div>}

          {/* Related calculators */}
          {relatedCalculators.length > 0 && (
            <section className="mt-10 pt-8" style={{ borderTop: '1px solid var(--surface-container-high)' }}>
              <h2 className="text-[14px] uppercase tracking-widest font-bold mb-4" style={{ color: 'var(--text-tertiary)' }}>
                Related calculators
              </h2>
              <div className="flex flex-wrap gap-3">
                {relatedCalculators.map((c) => (
                  <Link
                    key={c.path}
                    to={c.path}
                    className="flex items-center gap-2 px-4 py-2.5 rounded hover:border-[#0078d4] hover:text-[#0078d4] transition-colors text-[14px]"
                    style={{
                      backgroundColor: 'var(--surface-container)',
                      border: '1px solid var(--outline-variant)',
                      color: 'var(--text-secondary)'
                    }}
                  >
                    <span className="material-symbols-outlined text-[#0078d4] text-[18px]">
                      calculate
                    </span>
                    {c.label}
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
